import React, { useState } from "react";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronUp,
  Info,
  MessageCircle,
  Mic,
  Paperclip,
  Plus,
  Loader2,
  Search,
  X,
} from "lucide-react";
import { FaInstagram } from "react-icons/fa";
import { CreativeMedia, DEFAULT_BRIEF } from "./types";
import { ReachEstimate } from "./api";

// ── buttons ──────────────────────────────────────────────────────────────
// Literal blue, not `bg-primary-700` — `primary-*` isn't defined in
// tailwind.config.js repo-wide, so that class compiled to no background at
// all (this is the "Continue"/"Launch"/"Save" button across every step).
export const primaryBtn =
  "px-4 py-2.5 text-sm font-medium whitespace-nowrap text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors disabled:opacity-50";
export const ghostBtn =
  "px-4 py-2.5 text-sm font-medium whitespace-nowrap text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors";
export const inputCls =
  "w-full px-4 py-3 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/30";

// ── form controls ────────────────────────────────────────────────────────
export const Field: React.FC<{ label: string; hint?: string; className?: string; children: React.ReactNode }> = ({
  label,
  hint,
  className,
  children,
}) => (
  <div className={className}>
    <label className="block text-sm text-gray-800 dark:text-gray-200">{label}</label>
    {hint && <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
    <div className="mt-2">{children}</div>
  </div>
);

export const SelectBox: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
  icon?: React.ReactNode;
  prefix?: string;
}> = ({ value, onChange, options, placeholder, icon, prefix }) => (
  <div className="relative">
    {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">{icon}</span>}
    {prefix && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">{prefix}</span>}
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputCls} appearance-none pr-10 ${icon ? "pl-11" : ""} ${prefix ? "pl-[4.25rem]" : ""} ${value ? "" : "text-gray-400"}`}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
    <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-700 dark:text-gray-300 pointer-events-none" />
  </div>
);

export const RadioCard: React.FC<{
  selected: boolean;
  onClick: () => void;
  title: string;
  badge?: string;
  children?: React.ReactNode;
}> = ({ selected, onClick, title, badge, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left w-full rounded-xl px-4 py-4 flex items-start gap-3 transition-colors bg-white dark:bg-gray-900 ${
      // `primary-*` isn't defined in tailwind.config.js (repo-wide, see other
      // fixed spots on this page) — literal blue here so the selected state
      // is actually visible instead of silently compiling to no color.
      selected ? "border-2 border-blue-600" : "border border-gray-300 dark:border-gray-700 hover:border-blue-300"
    }`}
  >
    <span
      className={`mt-1 w-5 h-5 shrink-0 rounded-full border flex items-center justify-center ${
        selected ? "border-blue-600" : "border-gray-300 dark:border-gray-600"
      }`}
    >
      {selected && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
    </span>
    <span className="min-w-0">
      <span className="flex items-center gap-2 flex-wrap">
        <span className={`text-lg ${selected ? "text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"}`}>{title}</span>
        {badge && <span className="text-xs text-gray-600 dark:text-gray-300 bg-primary-50 dark:bg-primary-500/15 rounded px-1.5 py-0.5">{badge}</span>}
      </span>
      <span className={`block text-sm ${selected ? "text-gray-600 dark:text-gray-300" : "text-gray-500 dark:text-gray-400"}`}>{children}</span>
    </span>
  </button>
);

export const RemovableChip: React.FC<{ label: string; onRemove: () => void }> = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200 border border-gray-400 dark:border-gray-600 bg-primary-50/60 dark:bg-primary-500/10 rounded-md px-2.5 py-1.5">
    {label}
    <button type="button" onClick={onRemove} title={`Remove ${label}`} className="text-gray-700 dark:text-gray-200 hover:text-rose-600">
      <X size={14} />
    </button>
  </span>
);

export const InfoChip: React.FC<{ label?: string; value: string }> = ({ label, value }) => (
  <span className="inline-block text-sm bg-primary-50 dark:bg-primary-500/15 rounded px-1.5 py-0.5 text-gray-700 dark:text-gray-200">
    {label && <span className="text-gray-900 dark:text-gray-100">{label}: </span>}
    {value}
  </span>
);

export const InfoNote: React.FC<{ title?: string; icon?: boolean; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="bg-primary-50 dark:bg-primary-500/10 rounded-md px-3 py-2.5 flex items-start gap-2.5">
    {icon && <Info size={20} className="text-primary-700 shrink-0 mt-0.5" />}
    <div>
      {title && <p className="text-sm text-gray-900 dark:text-gray-100">{title}</p>}
      <p className="text-sm text-gray-800 dark:text-gray-200">{children}</p>
    </div>
  </div>
);

// Search box that adds free-typed or suggested entries as removable chips —
// used only for Saved Segments (client-side substring match over a fixed
// local list; segments aren't sent to the API, see types.ts).
export const TagPicker: React.FC<{
  label: string;
  placeholder: string;
  suggestions: string[];
  value: string[];
  onChange: (next: string[]) => void;
}> = ({ label, placeholder, suggestions, value, onChange }) => {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const matches = suggestions.filter((s) => !value.includes(s) && s.toLowerCase().includes(query.toLowerCase()));
  const add = (v: string) => {
    const t = v.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setQuery("");
  };
  return (
    <div>
      <label className="block text-sm text-gray-800 dark:text-gray-200">{label}</label>
      <div className="relative mt-2">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 120)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add(query);
            }
          }}
          placeholder={placeholder}
          className={`${inputCls} pl-10`}
        />
        {focused && matches.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full max-h-40 overflow-y-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1">
            {matches.map((m) => (
              <li key={m}>
                <button type="button" onMouseDown={() => add(m)} className="w-full text-left px-4 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800">
                  {m}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {value.map((v) => (
            <RemovableChip key={v} label={v} onRemove={() => onChange(value.filter((x) => x !== v))} />
          ))}
        </div>
      )}
    </div>
  );
};

// A generic {key,label} pair — every real search/list endpoint (locations,
// interests, behaviors, locales, custom audiences) returns items shaped
// close enough to this that one picker UI can cover all of them.
export interface PickItem {
  key: string;
  label: string;
}

// Checkbox list over a fixed set of items, filtered client-side as you type
// — Saved Segments (plain strings) and Custom Audiences (real {id,name}
// from the API) both fit this once wrapped as PickItem.
export const ListPicker: React.FC<{
  placeholder: string;
  options: PickItem[];
  value: string[]; // selected keys
  onChange: (next: string[]) => void;
  loading?: boolean;
  emptyLabel?: string;
}> = ({ placeholder, options, value, onChange, loading, emptyLabel }) => {
  const [query, setQuery] = useState("");
  const shown = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));
  const toggle = (key: string) => onChange(value.includes(key) ? value.filter((x) => x !== key) : [...value, key]);
  return (
    <div>
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} className={`${inputCls} pl-10`} />
      </div>
      <div className="mt-3 space-y-1">
        {loading && <p className="text-sm text-gray-400 px-2 py-1.5 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>}
        {!loading &&
          shown.map((o) => (
            <label key={o.key} className="flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
              <input type="checkbox" checked={value.includes(o.key)} onChange={() => toggle(o.key)} className="w-4 h-4 rounded border-gray-300 text-primary-700 focus:ring-primary-500" />
              {o.label}
            </label>
          ))}
        {!loading && shown.length === 0 && <p className="text-sm text-gray-400 px-2 py-1.5">{emptyLabel || "No matches."}</p>}
      </div>
    </div>
  );
};

// Live search → removable chips (locations, interests, behaviors). `query`
// is controlled by the caller so it can drive a debounced API hook.
export const AsyncChipPicker: React.FC<{
  label: string;
  placeholder: string;
  query: string;
  onQueryChange: (q: string) => void;
  options: PickItem[];
  loading?: boolean;
  value: PickItem[];
  onChange: (next: PickItem[]) => void;
  minChars?: number;
}> = ({ label, placeholder, query, onQueryChange, options, loading, value, onChange, minChars = 2 }) => {
  const [focused, setFocused] = useState(false);
  const selectedKeys = new Set(value.map((v) => v.key));
  const matches = options.filter((o) => !selectedKeys.has(o.key));
  const add = (item: PickItem) => {
    onChange([...value, item]);
    onQueryChange("");
  };
  return (
    <div>
      <label className="block text-sm text-gray-800 dark:text-gray-200">{label}</label>
      <div className="relative mt-2">
        {loading ? (
          <Loader2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 animate-spin" />
        ) : (
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        )}
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={placeholder}
          className={`${inputCls} pl-10`}
        />
        {focused && query.length >= minChars && (
          <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1">
            {matches.length === 0 && !loading && <li className="px-4 py-2 text-sm text-gray-400">No matches.</li>}
            {matches.map((m) => (
              <li key={m.key}>
                <button type="button" onMouseDown={() => add(m)} className="w-full text-left px-4 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800">
                  {m.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {value.map((v) => (
            <RemovableChip key={v.key} label={v.label} onRemove={() => onChange(value.filter((x) => x.key !== v.key))} />
          ))}
        </div>
      )}
    </div>
  );
};

// Live search, single choice — languages.
export const AsyncSingleSelect: React.FC<{
  query: string;
  onQueryChange: (q: string) => void;
  options: PickItem[];
  loading?: boolean;
  value: PickItem | null;
  onChange: (item: PickItem) => void;
  placeholder?: string;
  minChars?: number;
}> = ({ query, onQueryChange, options, loading, value, onChange, placeholder, minChars = 2 }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div className="relative">
      {loading ? (
        <Loader2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 animate-spin" />
      ) : (
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
      )}
      <input
        value={focused ? query : value?.label || query}
        onChange={(e) => onQueryChange(e.target.value)}
        onFocus={() => {
          setFocused(true);
          onQueryChange("");
        }}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder={placeholder || "Search languages"}
        className={`${inputCls} pl-10`}
      />
      {focused && query.length >= minChars && (
        <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1">
          {options.length === 0 && !loading && <li className="px-4 py-2 text-sm text-gray-400">No matches.</li>}
          {options.map((o) => (
            <li key={o.key}>
              <button
                type="button"
                onMouseDown={() => {
                  onChange(o);
                  onQueryChange("");
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── page footer ──────────────────────────────────────────────────────────
export const StepFooter: React.FC<{
  onBack?: () => void;
  onNext: () => void;
  nextLabel: string;
  onSaveDraft?: () => void;
  savingDraft?: boolean;
  nextDisabled?: boolean;
}> = ({ onBack, onNext, nextLabel, onSaveDraft, savingDraft, nextDisabled }) => (
  <div className="flex items-center justify-between gap-3 mt-10 flex-wrap">
    <button
      type="button"
      className={ghostBtn}
      disabled={savingDraft}
      onClick={onSaveDraft || (() => toast.info("Drafts can't be saved yet — no ads API connected."))}
    >
      {savingDraft ? "Saving…" : "Save as Draft"}
    </button>
    <div className="flex items-center gap-2">
      {onBack && (
        <button type="button" className={ghostBtn} onClick={onBack}>
          Back
        </button>
      )}
      <button type="button" className={primaryBtn} onClick={onNext} disabled={nextDisabled}>
        {nextLabel}
      </button>
    </div>
  </div>
);

export const SectionHeader: React.FC<{ title: string; subtitle: string; action?: React.ReactNode }> = ({ title, subtitle, action }) => (
  <div className="flex items-start justify-between gap-4 mb-6">
    <div>
      <h2 className="text-2xl text-gray-900 dark:text-gray-100">{title}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
    </div>
    {action}
  </div>
);

export const AddButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button type="button" onClick={onClick} className={`${ghostBtn} flex items-center gap-3 shrink-0`}>
    {label} <Plus size={18} />
  </button>
);

// ── right-hand panels ────────────────────────────────────────────────────
export const InstagramBadge: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <span
    className="inline-flex items-center justify-center rounded-md bg-gradient-to-br from-fuchsia-200 to-pink-100"
    style={{ width: size, height: size }}
  >
    <FaInstagram size={size * 0.55} className="text-pink-600" />
  </span>
);

export const PanelShell: React.FC<{ title: React.ReactNode; onClose?: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <aside className="relative bg-white dark:bg-gray-900 border-l border-gray-100 dark:border-gray-800 flex flex-col min-h-0">
    <div className="bg-lime-50 dark:bg-gray-800 px-5 py-4 flex items-center justify-between">
      <h3 className="text-xl text-gray-900 dark:text-gray-100 flex items-center gap-1.5">{title}</h3>
      {onClose && (
        <button type="button" onClick={onClose} title="Close" className="text-gray-800 dark:text-gray-200">
          <X size={20} />
        </button>
      )}
    </div>
    {children}
  </aside>
);

interface ChatMessage {
  from: "ai" | "user";
  text: string;
}

export const AiPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const short = DEFAULT_BRIEF.length > 80 ? `${DEFAULT_BRIEF.slice(0, 80)}…` : DEFAULT_BRIEF;
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      from: "ai",
      text: `I've populated the campaign details based on: "${short}". Feel free to tweak any field — ask me to adjust tone, audience or timing.`,
    },
  ]);
  const [draft, setDraft] = useState("");

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    setMessages((prev) => [
      ...prev,
      { from: "user", text },
      { from: "ai", text: "I can't change the campaign from here yet — ConvoAI isn't connected to this flow." },
    ]);
    setDraft("");
  };

  return (
    <PanelShell
      title={
        <>
          <MessageCircle size={22} /> ConvoAI
        </>
      }
      onClose={onClose}
    >
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`text-sm rounded-xl px-4 py-3 border ${
              m.from === "ai"
                ? "border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-900"
                : "border-primary-100 dark:border-primary-500/30 bg-primary-50 dark:bg-primary-500/10 text-gray-800 dark:text-gray-100 ml-8"
            }`}
          >
            {m.text}
          </div>
        ))}
      </div>
      <div className="p-4">
        <div className="border border-gray-300 dark:border-gray-700 rounded-lg p-3">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="Ask anything. . ."
            className="w-full resize-none text-sm bg-transparent outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500"
          />
          <div className="flex items-center justify-end gap-3 mt-1">
            <Mic size={18} className="text-gray-700 dark:text-gray-300" />
            <Paperclip size={18} className="text-gray-700 dark:text-gray-300" />
            <button type="button" onClick={send} className="px-3 py-1.5 text-sm text-white bg-primary-700 hover:bg-primary-800 rounded-md">
              Send
            </button>
          </div>
        </div>
      </div>
    </PanelShell>
  );
};

const CollapsibleCard: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-4 shadow-sm">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-left">
        <span className="text-lg text-gray-900 dark:text-gray-100 flex items-center gap-2">{title}</span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
};

// Hardcoded: a real estimate will come from the ads API. Kept static rather
// than faked from the form so it can't imply a calculation that isn't there.
export const AudienceDefinitionCard: React.FC = () => (
  <CollapsibleCard title="Audience Definition">
    <div className="flex h-1 rounded-full overflow-hidden">
      <span className="flex-1 bg-rose-100" />
      <span className="flex-1 bg-amber-100" />
      <span className="flex-1 bg-emerald-500" />
    </div>
    <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mt-3">
      <span>Narrow</span>
      <span>Broad</span>
    </div>
    <div className="mt-3">
      <InfoNote>Your audience is broad, helping you reach more people likely to respond.</InfoNote>
    </div>
  </CollapsibleCard>
);

const compact = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K` : String(n));

// Real numbers from POST /instagram/ads/reach-estimate (api.ts) once the
// active ad set has at least one location; before that (or while loading)
// this shows a message instead of a number, rather than a fake range.
export const ReachCard: React.FC<{ loading?: boolean; estimate: ReachEstimate | null; needsLocation?: boolean }> = ({ loading, estimate, needsLocation }) => (
  <CollapsibleCard
    title={
      <>
        Estimated Daily Reach <InstagramBadge size={30} />
      </>
    }
  >
    {needsLocation ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">Add a location to this ad set to see an estimate.</p>
    ) : loading ? (
      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Estimating reach…
      </p>
    ) : estimate?.estimate_ready ? (
      <>
        <p className="text-lg text-primary-700/70 dark:text-primary-300">
          {compact(estimate.daily_reach_lower)} – {compact(estimate.daily_reach_upper)}
        </p>
        <div className="h-1 rounded-full bg-primary-100 dark:bg-primary-500/20 mt-3 overflow-hidden">
          <span
            className="block h-full bg-emerald-500"
            style={{ width: `${Math.min(100, Math.round((estimate.daily_reach_upper / Math.max(estimate.daily_active_users, 1)) * 100))}%` }}
          />
        </div>
      </>
    ) : (
      <p className="text-sm text-gray-500 dark:text-gray-400">Estimate not available yet.</p>
    )}
    <div className="mt-4">
      <InfoNote>Estimates are based on your budget, targeting, placements, and past performance. Actual results may vary.</InfoNote>
    </div>
  </CollapsibleCard>
);

export const PhonePreview: React.FC<{
  handle: string;
  media: CreativeMedia | null;
  primaryText: string;
  headline: string;
  cta: string;
}> = ({ handle, media, primaryText, headline, cta }) => (
  <div className="mx-auto w-[280px] rounded-[42px] bg-white dark:bg-gray-100 border-[10px] border-gray-100 dark:border-gray-300 shadow-lg px-3 pt-3 pb-5">
    <div className="flex items-center justify-between text-[10px] font-semibold text-gray-900 px-1">
      <span>9:41</span>
      <span className="flex gap-0.5 items-end">
        <span className="w-1 h-1.5 bg-gray-900 rounded-sm" />
        <span className="w-1 h-2 bg-gray-900 rounded-sm" />
        <span className="w-1 h-2.5 bg-gray-900 rounded-sm" />
      </span>
    </div>
    <p className="mt-6 text-sm font-semibold text-gray-900">{handle}</p>
    <div className="mt-2 aspect-square rounded-md bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center">
      {media ? (
        media.kind === "video" ? (
          <video src={media.url} className="w-full h-full object-cover" muted controls />
        ) : (
          <img src={media.url} alt={media.name} className="w-full h-full object-cover" />
        )
      ) : (
        <span className="text-xs text-teal-800 text-center px-4">Broadcast Creative Appears here</span>
      )}
    </div>
    <p className="text-[11px] text-gray-500 mt-1.5">Sponsored</p>
    <p className="text-xs text-gray-800 mt-0.5 break-words">{primaryText || "Upgrade your workspace with smarter customer engagement."}</p>
    <p className="text-xs font-semibold text-gray-900 mt-2 break-words">{headline || "Built for better customer conversations"}</p>
    <div className="mt-2 border border-gray-300 rounded-md py-2.5 px-3 text-sm font-medium text-gray-900 bg-white">{cta || "Shop Now"}</div>
  </div>
);

export const LivePreviewPanel: React.FC<{
  showReach?: boolean;
  showAudience?: boolean;
  showPhone?: boolean;
  phone?: React.ComponentProps<typeof PhonePreview>;
  reachLoading?: boolean;
  reachEstimate?: ReachEstimate | null;
  reachNeedsLocation?: boolean;
  onOpenAi: () => void;
}> = ({ showReach, showAudience, showPhone, phone, reachLoading, reachEstimate, reachNeedsLocation, onOpenAi }) => (
  <PanelShell title="Live Preview">
    <div className="relative flex-1 overflow-y-auto p-4 space-y-4">
      {showAudience && <AudienceDefinitionCard />}
      {showReach && <ReachCard loading={reachLoading} estimate={reachEstimate ?? null} needsLocation={reachNeedsLocation} />}
      {showPhone && phone && <PhonePreview {...phone} />}
    </div>
    <button
      type="button"
      onClick={onOpenAi}
      title="Open ConvoAI"
      className="absolute bottom-4 right-4 w-12 h-12 rounded-xl bg-emerald-300 hover:bg-emerald-400 text-gray-900 flex items-center justify-center shadow-md transition-colors"
    >
      <MessageCircle size={22} />
    </button>
  </PanelShell>
);
